import type { Theme } from '@mui/material/styles';

interface PrismScrollbarStylesOptions {
  thumbBorderColor?: string;
  thumbBorderWidth?: number | string;
  thumbColor?: string;
  trackColor?: string;
  width?: number | string;
}

const toCssValue = (value: number | string) => (
  typeof value === 'number' ? `${value}px` : value
);

export const getPrismScrollbarStyles = (
  theme: Theme,
  {
    thumbBorderColor = 'transparent',
    thumbBorderWidth = 2,
    thumbColor = theme.palette.neutral[300],
    trackColor = 'transparent',
    width = 8,
  }: PrismScrollbarStylesOptions = {}
) => {
  const scrollbarWidth = toCssValue(width);
  const scrollbarThumbBorderWidth = toCssValue(thumbBorderWidth);

  return {
    scrollbarColor: `${thumbColor} ${trackColor}`,
    scrollbarWidth: 'thin' as const,
    '&::-webkit-scrollbar': {
      height: scrollbarWidth,
      width: scrollbarWidth,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundClip: 'padding-box' as const,
      backgroundColor: thumbColor,
      border: `${scrollbarThumbBorderWidth} solid ${thumbBorderColor}`,
      borderRadius: theme.shape.radius.s,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: trackColor,
    },
  };
};

export const getPrismScrollbarTargetStyles = (
  theme: Theme,
  targetSelector: string,
  options: PrismScrollbarStylesOptions = {}
) => {
  const scrollbarStyles = getPrismScrollbarStyles(theme, options);
  const {
    '&::-webkit-scrollbar': scrollbar,
    '&::-webkit-scrollbar-thumb': scrollbarThumb,
    '&::-webkit-scrollbar-track': scrollbarTrack,
    ...baseStyles
  } = scrollbarStyles;

  return {
    [targetSelector]: baseStyles,
    [`${targetSelector}::-webkit-scrollbar`]: scrollbar,
    [`${targetSelector}::-webkit-scrollbar-thumb`]: scrollbarThumb,
    [`${targetSelector}::-webkit-scrollbar-track`]: scrollbarTrack,
  };
};
